// src/pages/Public/Register.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert, Image } from 'react-bootstrap';
import { FaUser, FaEnvelope, FaPhone, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import axios from '../../api/axios';
import logo from '../../assets/images/cac-logo.jpeg';

const Register = () => {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        first_name: '',
        last_name: '',
        phone: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [animated, setAnimated] = useState(false);
    const [logoError, setLogoError] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        // Masquer la navbar sur la page d'inscription
        const navbar = document.querySelector('.navbar-custom');
        if (navbar) {
            navbar.style.display = 'none';
        }

        setAnimated(true);

        // Restaurer la navbar quand on quitte la page
        return () => {
            if (navbar) {
                navbar.style.display = '';
            }
        };
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError('');
        setSuccess('');
    };

    const handleLogoError = () => {
        setLogoError(true);
    };

    const validateForm = () => {
        if (formData.password !== formData.confirmPassword) {
            setError('Les mots de passe ne correspondent pas');
            return false;
        }

        if (formData.password.length < 6) {
            setError('Le mot de passe doit contenir au moins 6 caractères');
            return false;
        }

        if (!formData.email.includes('@')) {
            setError('Veuillez entrer une adresse email valide');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);
        setError('');

        try {
            const response = await axios.post('/auth/register/', {
                email: formData.email,
                password: formData.password,
                first_name: formData.first_name,
                last_name: formData.last_name,
                phone: formData.phone
            });

            setSuccess('✅ Inscription réussie ! Redirection vers la connexion...');
            setTimeout(() => {
                navigate('/login');
            }, 2000);
        } catch (err) {
            console.error('Erreur inscription:', err);
            if (err.response?.data?.email) {
                setError(err.response.data.email[0]);
            } else if (err.response?.data?.password) {
                setError(err.response.data.password[0]);
            } else if (err.response?.data?.phone) {
                setError(err.response.data.phone[0]);
            } else {
                setError('Une erreur est survenue. Veuillez réessayer.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="register-page">
            <div className="register-bg"></div>

            {/* Lien retour accueil */}
            <Link to="/" className="back-home-link">
                <span>←</span> Retour à l'accueil
            </Link>

            <Container className="register-container">
                <Row className="justify-content-center align-items-center min-vh-100">
                    <Col lg={5} md={7} sm={9} xs={11}>
                        <div className={`register-card-wrapper ${animated ? 'slide-up' : ''}`}>
                            <Card className="register-card border-0 shadow-lg">
                                <Card.Body className="p-0">
                                    {/* Header avec Logo */}
                                    <div className="register-header">
                                        <div className="logo-container">
                                            {!logoError ? (
                                                <Image
                                                    src={logo}
                                                    alt="CAC Logo"
                                                    className="register-logo-img"
                                                    onError={handleLogoError}
                                                />
                                            ) : (
                                                <div className="logo-fallback">
                                                    <span>🚚</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="brand-text">
                                            <h2 className="brand-name">CAC</h2>
                                            <p className="brand-subtitle">Chine Afrique Cargo</p>
                                        </div>
                                        <h3 className="register-title">Créer votre compte</h3>
                                        <p className="register-subtitle">Rejoignez la communauté CAC</p>
                                    </div>

                                    {/* Formulaire */}
                                    <div className="register-form">
                                        {error && (
                                            <Alert variant="danger" className="alert-custom animate-fadeIn">
                                                <span className="alert-icon">⚠️</span>
                                                {error}
                                            </Alert>
                                        )}

                                        {success && (
                                            <Alert variant="success" className="alert-custom-success animate-fadeIn">
                                                <span className="alert-icon">✅</span>
                                                {success}
                                            </Alert>
                                        )}

                                        <Form onSubmit={handleSubmit}>
                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="form-group">
                                                        <div className="input-wrapper">
                                                            <FaUser className="input-icon" />
                                                            <Form.Control
                                                                type="text"
                                                                name="first_name"
                                                                value={formData.first_name}
                                                                onChange={handleChange}
                                                                placeholder="Prénom"
                                                                className="form-control-custom"
                                                            />
                                                        </div>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="form-group">
                                                        <div className="input-wrapper">
                                                            <FaUser className="input-icon" />
                                                            <Form.Control
                                                                type="text"
                                                                name="last_name"
                                                                value={formData.last_name}
                                                                onChange={handleChange}
                                                                placeholder="Nom"
                                                                className="form-control-custom"
                                                            />
                                                        </div>
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <Form.Group className="form-group">
                                                <div className="input-wrapper">
                                                    <FaEnvelope className="input-icon" />
                                                    <Form.Control
                                                        type="email"
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        placeholder="Adresse email"
                                                        required
                                                        className="form-control-custom"
                                                    />
                                                </div>
                                            </Form.Group>

                                            <Form.Group className="form-group">
                                                <div className="input-wrapper">
                                                    <FaPhone className="input-icon" />
                                                    <Form.Control
                                                        type="tel"
                                                        name="phone"
                                                        value={formData.phone}
                                                        onChange={handleChange}
                                                        placeholder="Numéro de téléphone"
                                                        className="form-control-custom"
                                                    />
                                                </div>
                                            </Form.Group>

                                            <Form.Group className="form-group">
                                                <div className="input-wrapper">
                                                    <FaLock className="input-icon" />
                                                    <Form.Control
                                                        type={showPassword ? "text" : "password"}
                                                        name="password"
                                                        value={formData.password}
                                                        onChange={handleChange}
                                                        placeholder="Mot de passe"
                                                        required
                                                        className="form-control-custom"
                                                    />
                                                    <button
                                                        type="button"
                                                        className="password-toggle"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        tabIndex={-1}
                                                        aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                                                    >
                                                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                                                    </button>
                                                </div>
                                                <Form.Text className="password-hint">
                                                    Minimum 6 caractères
                                                </Form.Text>
                                            </Form.Group>

                                            <Form.Group className="form-group">
                                                <div className="input-wrapper">
                                                    <FaLock className="input-icon" />
                                                    <Form.Control
                                                        type={showConfirmPassword ? "text" : "password"}
                                                        name="confirmPassword"
                                                        value={formData.confirmPassword}
                                                        onChange={handleChange}
                                                        placeholder="Confirmer le mot de passe"
                                                        required
                                                        className="form-control-custom"
                                                    />
                                                    <button
                                                        type="button"
                                                        className="password-toggle"
                                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                        tabIndex={-1}
                                                        aria-label={showConfirmPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                                                    >
                                                        {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                                    </button>
                                                </div>
                                            </Form.Group>

                                            <Button
                                                type="submit"
                                                disabled={loading}
                                                className="register-btn"
                                            >
                                                {loading ? (
                                                    <>
                                                        <div className="spinner-custom"></div>
                                                        <span>Inscription en cours...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span>📝</span> S'inscrire
                                                    </>
                                                )}
                                            </Button>

                                            <div className="register-footer">
                                                <p className="mb-0">
                                                    Déjà un compte ?{' '}
                                                    <Link to="/login" className="login-link">
                                                        Se connecter
                                                    </Link>
                                                </p>
                                            </div>
                                        </Form>
                                    </div>
                                </Card.Body>
                            </Card>

                            {/* Footer */}
                            <div className="register-page-footer">
                                <p className="copyright">
                                    © {new Date().getFullYear()} CAC - Chine Afrique Cargo
                                </p>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>

            <style>{`
                .register-page {
                    min-height: 100vh;
                    position: relative;
                    overflow: hidden;
                }
                
                .register-bg {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
                    z-index: -1;
                }
                
                .register-bg::before {
                    content: '';
                    position: absolute;
                    top: -50%;
                    left: -50%;
                    width: 200%;
                    height: 200%;
                    background: radial-gradient(circle at 30% 40%, rgba(198, 40, 40, 0.15) 0%, transparent 50%);
                    animation: bgPulse 15s ease-in-out infinite;
                    will-change: transform, opacity;
                }
                
                .register-bg::after {
                    content: '';
                    position: absolute;
                    bottom: -50%;
                    right: -50%;
                    width: 200%;
                    height: 200%;
                    background: radial-gradient(circle at 70% 60%, rgba(249, 168, 37, 0.1) 0%, transparent 50%);
                    animation: bgPulse 15s ease-in-out infinite reverse;
                    will-change: transform, opacity;
                }
                
                @keyframes bgPulse {
                    0%, 100% { transform: scale(1); opacity: 0.5; }
                    50% { transform: scale(1.1); opacity: 0.8; }
                }
                
                /* Animation fadeIn */
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                
                .animate-fadeIn {
                    animation: fadeIn 0.3s ease forwards;
                }
                
                /* Back to home link */
                .back-home-link {
                    position: absolute;
                    top: 24px;
                    left: 24px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    color: rgba(255, 255, 255, 0.7);
                    text-decoration: none;
                    font-size: 14px;
                    font-weight: 500;
                    padding: 8px 16px;
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 40px;
                    backdrop-filter: blur(10px);
                    transition: all 0.3s ease;
                    z-index: 10;
                    will-change: transform, background;
                }
                
                .back-home-link:hover {
                    background: rgba(255, 255, 255, 0.2);
                    color: white;
                    transform: translateX(-4px);
                }
                
                .back-home-link span {
                    font-size: 18px;
                    transition: transform 0.3s ease;
                }
                
                .back-home-link:hover span {
                    transform: translateX(-4px);
                }
                
                .register-container {
                    position: relative;
                    z-index: 1;
                }
                
                .register-card-wrapper {
                    opacity: 0;
                    transform: translateY(30px);
                    will-change: transform, opacity;
                }
                
                .register-card-wrapper.slide-up {
                    animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
                
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .register-card {
                    border-radius: 32px;
                    overflow: hidden;
                    backdrop-filter: blur(10px);
                    background: rgba(255, 255, 255, 0.98);
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                }
                
                /* Header avec Logo */
                .register-header {
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    padding: 35px 30px 30px;
                    text-align: center;
                    color: white;
                    position: relative;
                    overflow: hidden;
                }
                
                .register-header::before {
                    content: '';
                    position: absolute;
                    top: -50%;
                    right: -50%;
                    width: 200px;
                    height: 200px;
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 50%;
                }
                
                .register-header::after {
                    content: '';
                    position: absolute;
                    bottom: -30%;
                    left: -20%;
                    width: 150px;
                    height: 150px;
                    background: rgba(255, 255, 255, 0.05);
                    border-radius: 50%;
                }
                
                .logo-container {
                    display: flex;
                    justify-content: center;
                    margin-bottom: 16px;
                    position: relative;
                    z-index: 1;
                }
                
                .register-logo-img {
                    width: 75px;
                    height: 75px;
                    object-fit: contain;
                    border-radius: 20px;
                    background: white;
                    padding: 8px;
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
                    transition: transform 0.3s ease;
                    will-change: transform;
                }
                
                .register-logo-img:hover {
                    transform: scale(1.05);
                }
                
                .logo-fallback {
                    width: 75px;
                    height: 75px;
                    background: white;
                    border-radius: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 36px;
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
                    animation: pulse 2s infinite;
                }
                
                @keyframes pulse {
                    0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4); }
                    70% { box-shadow: 0 0 0 15px rgba(255, 255, 255, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
                }
                
                .brand-text {
                    position: relative;
                    z-index: 1;
                    margin-bottom: 16px;
                }
                
                .brand-name {
                    font-size: 30px;
                    font-weight: 800;
                    margin: 0;
                    color: white;
                    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
                    letter-spacing: -0.5px;
                }
                
                .brand-subtitle {
                    font-size: 11px;
                    opacity: 0.9;
                    margin: 4px 0 0;
                    text-transform: uppercase;
                    letter-spacing: 2px;
                }
                
                .register-title {
                    font-size: 22px;
                    font-weight: 600;
                    margin-bottom: 6px;
                    position: relative;
                    z-index: 1;
                }
                
                .register-subtitle {
                    font-size: 14px;
                    opacity: 0.85;
                    margin: 0;
                    position: relative;
                    z-index: 1;
                }
                
                /* Formulaire */
                .register-form {
                    padding: 32px 28px;
                }
                
                .form-group {
                    margin-bottom: 20px;
                    position: relative;
                }
                
                .input-wrapper {
                    position: relative;
                }
                
                .input-icon {
                    position: absolute;
                    left: 16px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #C62828;
                    font-size: 15px;
                    z-index: 1;
                    transition: color 0.3s ease;
                }
                
                .form-control-custom {
                    width: 100%;
                    padding: 13px 16px 13px 45px;
                    border: 2px solid #E2E8F0;
                    border-radius: 14px;
                    font-size: 14px;
                    transition: all 0.3s ease;
                    background: white;
                    will-change: border-color, box-shadow;
                }
                
                .form-control-custom::placeholder {
                    color: #94A3B8;
                    opacity: 1;
                }
                
                .form-control-custom:focus {
                    border-color: #C62828;
                    outline: none;
                    box-shadow: 0 0 0 4px rgba(198, 40, 40, 0.1);
                }
                
                .form-control-custom:focus + .input-icon,
                .input-wrapper:focus-within .input-icon {
                    color: #F9A825;
                }
                
                .password-toggle {
                    position: absolute;
                    right: 16px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: none;
                    border: none;
                    color: #94A3B8;
                    cursor: pointer;
                    padding: 6px;
                    font-size: 15px;
                    border-radius: 8px;
                    transition: all 0.2s ease;
                    will-change: color, background;
                }
                
                .password-toggle:hover {
                    color: #C62828;
                    background: rgba(198, 40, 40, 0.05);
                }
                
                .password-toggle:focus-visible {
                    outline: 2px solid #C62828;
                    outline-offset: 2px;
                }
                
                .password-hint {
                    display: block;
                    margin-top: 6px;
                    font-size: 12px;
                    color: #94A3B8;
                    margin-left: 16px;
                }
                
                .alert-custom {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    background: #FEF2F2;
                    border: 1px solid #FECACA;
                    border-radius: 14px;
                    color: #DC2626;
                    font-size: 14px;
                    padding: 14px 18px;
                    margin-bottom: 24px;
                }
                
                .alert-custom-success {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    background: #F0FDF4;
                    border: 1px solid #BBF7D0;
                    border-radius: 14px;
                    color: #16A34A;
                    font-size: 14px;
                    padding: 14px 18px;
                    margin-bottom: 24px;
                }
                
                .alert-icon {
                    font-size: 18px;
                }
                
                .register-btn {
                    width: 100%;
                    padding: 15px;
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    border: none;
                    border-radius: 14px;
                    font-weight: 600;
                    font-size: 16px;
                    color: white;
                    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    position: relative;
                    overflow: hidden;
                    margin-top: 24px;
                    will-change: transform, box-shadow;
                }
                
                .register-btn::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
                    transition: left 0.5s ease;
                }
                
                .register-btn:hover:not(:disabled)::before {
                    left: 100%;
                }
                
                .register-btn:hover:not(:disabled) {
                    transform: translateY(-3px);
                    box-shadow: 0 15px 30px rgba(198, 40, 40, 0.3);
                }
                
                .register-btn:active:not(:disabled) {
                    transform: translateY(-1px);
                }
                
                .register-btn:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }
                
                .register-btn:focus-visible {
                    outline: 2px solid white;
                    outline-offset: 2px;
                }
                
                /* Spinner personnalisé */
                .spinner-custom {
                    width: 18px;
                    height: 18px;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-top-color: white;
                    border-radius: 50%;
                    animation: spin 0.8s linear infinite;
                }
                
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                
                .register-footer {
                    text-align: center;
                    margin-top: 24px;
                    padding-top: 20px;
                    border-top: 1px solid #E2E8F0;
                }
                
                .register-footer p {
                    color: #64748B;
                    font-size: 14px;
                }
                
                .login-link {
                    color: #C62828;
                    text-decoration: none;
                    font-weight: 700;
                    transition: all 0.3s ease;
                    margin-left: 4px;
                    padding: 2px 6px;
                    border-radius: 6px;
                }
                
                .login-link:hover {
                    color: #F9A825;
                    text-decoration: underline;
                    background: rgba(198, 40, 40, 0.05);
                }
                
                .login-link:focus-visible {
                    outline: 2px solid #C62828;
                    outline-offset: 2px;
                }
                
                /* Footer */
                .register-page-footer {
                    text-align: center;
                    margin-top: 20px;
                }
                
                .copyright {
                    color: rgba(255, 255, 255, 0.5);
                    font-size: 12px;
                }
                
                /* Responsive */
                @media (max-width: 768px) {
                    .register-header {
                        padding: 28px 20px 24px;
                    }
                    
                    .register-form {
                        padding: 28px 20px;
                    }
                    
                    .register-logo-img,
                    .logo-fallback {
                        width: 65px;
                        height: 65px;
                        font-size: 32px;
                    }
                    
                    .brand-name {
                        font-size: 26px;
                    }
                    
                    .register-title {
                        font-size: 20px;
                    }
                    
                    .form-control-custom {
                        padding: 12px 16px 12px 42px;
                    }
                    
                    .back-home-link {
                        top: 16px;
                        left: 16px;
                        padding: 6px 14px;
                        font-size: 13px;
                    }
                }
                
                @media (max-width: 480px) {
                    .register-header {
                        padding: 24px 16px 20px;
                    }
                    
                    .register-form {
                        padding: 24px 16px;
                    }
                    
                    .register-logo-img,
                    .logo-fallback {
                        width: 55px;
                        height: 55px;
                        font-size: 28px;
                        border-radius: 16px;
                    }
                    
                    .brand-name {
                        font-size: 22px;
                    }
                    
                    .brand-subtitle {
                        font-size: 9px;
                    }
                    
                    .register-title {
                        font-size: 18px;
                    }
                    
                    .register-subtitle {
                        font-size: 13px;
                    }
                    
                    .form-control-custom {
                        padding: 11px 16px 11px 40px;
                        font-size: 13px;
                    }
                    
                    .register-btn {
                        padding: 13px;
                        font-size: 15px;
                    }
                }
            `}</style>
        </div>
    );
};

export default Register;